import { cn } from '@/lib/utils';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useZoneSystem, usePerformancePaceZones } from '@/hooks/useClosedLoopAnalytics';

interface PaceZonesHudProps {
  className?: string;
}

// Polarized target % by zone number (Seiler 2010): 75% easy / 7% moderate / 18% hard
const POLARIZED_TARGET_PCT: Record<number, number> = { 1: 30, 2: 45, 3: 7, 4: 12, 5: 6 };

const ZONE_COLORS: Record<number, string> = {
  1: 'hsl(170, 80%, 45%)',
  2: 'hsl(160, 70%, 50%)',
  3: 'hsl(50, 80%, 55%)',
  4: 'hsl(35, 85%, 55%)',
  5: 'hsl(15, 85%, 55%)',
  6: 'hsl(350, 80%, 55%)',
  7: 'hsl(280, 65%, 60%)',
};

function formatPaceBound(val: number): string {
  const mins = Math.floor(val);
  const secs = Math.round((val - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Convert m/s speed to min/km pace */
function speedToPace(speedMs: number): number {
  if (speedMs <= 0) return 99;
  return 1000 / speedMs / 60; // min/km
}

const SOURCE_LABELS: Record<string, string> = {
  configured: 'MANUALLY SET',
  inferred: 'ESTIMATED FROM ACTIVITIES',
  vdot: 'VDOT CALIBRATED',
};

export function PaceZonesHud({ className }: PaceZonesHudProps) {
  const { formatPaceFromMinPerKm, paceUnit, formatPaceRange } = useUnitSystem();
  const { data: zoneSystem, isLoading: zsLoading } = useZoneSystem('run', 'pace');
  const { data: perfZones, isLoading: perfLoading } = usePerformancePaceZones();

  const isLoading = zsLoading || perfLoading;

  // Primary: zone system bands. Fallback: performance analytics pace_zone_definitions.
  const { zones, sourceLabel } = (() => {
    // Try zone system first
    if (zoneSystem?.bands && zoneSystem.bands.length > 0) {
      const built = zoneSystem.bands.map((band) => ({
        id: band.zone_number,
        label: `Z${band.zone_number} · ${band.name.toUpperCase()}`,
        paceRange: `${formatPaceBound(band.lower_bound)}–${formatPaceBound(band.upper_bound)}`,
        pct: POLARIZED_TARGET_PCT[band.zone_number] ?? Math.round(100 / zoneSystem.bands.length),
        color: band.color ?? ZONE_COLORS[band.zone_number] ?? 'hsl(210, 30%, 50%)',
      }));
      const anchorDisplay = (() => {
        if (zoneSystem.anchor_value == null) return '—';
        if (zoneSystem.method === 'vdot') return `VDOT ${zoneSystem.anchor_value.toFixed(1)}`;
        const minPerKm = zoneSystem.anchor_value / 60;
        return `LT2 ${formatPaceFromMinPerKm(minPerKm)}`;
      })();
      return {
        zones: built,
        sourceLabel: `[SYS] ${zoneSystem.method.toUpperCase()}-DERIVED · ${anchorDisplay}`,
      };
    }

    // Fallback: performance analytics pace_zone_definitions
    if (perfZones && perfZones.zones.length > 0) {
      const built = perfZones.zones.map((pz, idx) => {
        const zoneNum = idx + 1;
        const minPace = pz.min_speed_ms != null ? speedToPace(pz.min_speed_ms) : null;
        const maxPace = pz.max_speed_ms != null ? speedToPace(pz.max_speed_ms) : null;

        // For display: slower pace = higher min/km number, faster = lower
        // min_speed_ms → max_pace (slowest), max_speed_ms → min_pace (fastest)
        let paceRange: string;
        if (zoneNum === 1 && maxPace != null) {
          // Z1 Easy: slower than maxPace
          paceRange = `> ${formatPaceBound(maxPace)}`;
        } else if (zoneNum === perfZones.zones.length && minPace != null) {
          // Z5 VO2max: faster than minPace
          paceRange = `< ${formatPaceBound(minPace)}`;
        } else if (minPace != null && maxPace != null) {
          // Middle zones: faster – slower
          paceRange = `${formatPaceBound(minPace)}–${formatPaceBound(maxPace)}`;
        } else {
          paceRange = '—';
        }

        return {
          id: zoneNum,
          label: `Z${zoneNum} · ${pz.name.toUpperCase()}`,
          paceRange,
          pct: POLARIZED_TARGET_PCT[zoneNum] ?? 20,
          color: ZONE_COLORS[zoneNum] ?? 'hsl(210, 30%, 50%)',
        };
      });

      const src = perfZones.threshold_pace_source;
      const srcText = src ? SOURCE_LABELS[src] ?? src.toUpperCase() : 'PERFORMANCE DATA';
      let anchorText = '';
      if (perfZones.threshold_pace_ms != null && perfZones.threshold_pace_ms > 0) {
        const ltPace = speedToPace(perfZones.threshold_pace_ms);
        anchorText = ` · LT2 ${formatPaceFromMinPerKm(ltPace)}`;
      }
      return {
        zones: built,
        sourceLabel: `[SYS] ${srcText}${anchorText}`,
      };
    }

    return { zones: null, sourceLabel: '[SYS] NOT CALIBRATED' };
  })();

  const isFromApi = zones !== null;

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-4',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          PACE ZONE DISTRIBUTION
        </span>
        <span className="ml-auto text-[8px] font-mono tracking-wider text-white/20 uppercase">
          {isLoading ? 'LOADING' : isFromApi ? 'PERSISTED' : 'READ ONLY'}
        </span>
      </div>

      {/* Zone bars */}
      {isFromApi && zones ? (
      <div className="space-y-2.5">
        {zones.map((zone) => (
          <div key={zone.id} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono tracking-wider text-[#A0A0A0]">
                {zone.label}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono tabular-nums text-white/40">
                  {zone.paceRange.includes('>') || zone.paceRange.includes('<')
                    ? zone.paceRange.replace(/[\d]+:[\d]+/g, (m) => `${formatPaceRange(m)}`)
                    : `${formatPaceRange(zone.paceRange)}${paceUnit}`
                  }
                </span>
                <span className="text-[10px] font-mono tabular-nums text-foreground w-7 text-right">
                  {zone.pct}%
                </span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${zone.pct}%`,
                  backgroundColor: zone.color,
                  boxShadow: `0 0 8px ${zone.color}40`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="py-4 text-center">
          <p className="text-[9px] font-mono text-white/30 tracking-wider">
            Add race results in Settings to calibrate zones
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[9px] font-mono text-white/25 tracking-wider text-center uppercase">
          {sourceLabel}
        </p>
      </div>
    </div>
  );
}
